import { useRouter } from 'next/router'
import React, { useCallback, useMemo } from 'react'

import { Icon } from '@/components/Icon/Icon'
import { useDialog } from '@/components/Dialog/DialogContext'
import { QuillIcon } from '@/shared/components/QuillIcon/QuillIcon'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { pxToRem } from '@/shared/styles/themes'
import { faHouse } from '@fortawesome/pro-light-svg-icons/faHouse'
import { faBookOpen } from '@fortawesome/pro-light-svg-icons/faBookOpen'
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser'
import { faHouse as faHouseSolid } from '@fortawesome/pro-solid-svg-icons/faHouse'
import { faBookOpen as faBookOpenSolid } from '@fortawesome/pro-solid-svg-icons/faBookOpen'
import { faUser as faUserSolid } from '@fortawesome/pro-solid-svg-icons/faUser'
import { styled } from '@mui/material'
import { AuthDialog } from 'fragments/Auth/AuthDialog'

type TabItem = {
  label: string
  href: string
  requiresAuth?: boolean
} & (
  | { icon: typeof faHouse; activeIcon: typeof faHouseSolid; customIcon?: never }
  | { icon?: never; activeIcon?: never; customIcon: (props: { active: boolean }) => React.ReactNode }
)

export const BottomTabBar: React.FC = () => {
  const router = useRouter()
  const { getAuthState, profile } = usePixieWorldAuthContext()
  const { openDialog } = useDialog()

  const authenticated = getAuthState() === 'signedIn'
  const isOnboarded = authenticated && profile?.is_onboarded

  const tabItems: TabItem[] = useMemo(() => [
    { label: 'Home', icon: faHouse, activeIcon: faHouseSolid, href: '/' },
    isOnboarded
      ? { label: 'Library', icon: faBookOpen, activeIcon: faBookOpenSolid, href: '/library' }
      : { label: 'Explore', icon: faBookOpen, activeIcon: faBookOpenSolid, href: '/shop' },
    {
      label: 'Create',
      customIcon: ({ active }) => (
        <QuillIconWrapper>
          <QuillIcon
            size={28}
            color={active ? '#F6a42A' : 'rgba(245, 242, 238, 0.6)'}
          />
        </QuillIconWrapper>
      ),
      href: '/create',
      requiresAuth: true,
    },
    authenticated
      ? { label: 'Profile', icon: faUser, activeIcon: faUserSolid, href: '/profile' }
      : { label: 'Sign In', icon: faUser, activeIcon: faUserSolid, href: '/login', requiresAuth: true },
  ], [authenticated, isOnboarded])

  const activeTab = useMemo(() => {
    const path = router.pathname

    if (path === '/') return '/'
    if (path.startsWith('/library')) return '/library'
    if (path.startsWith('/shop')) return '/shop'
    if (path.startsWith('/create')) return '/create'
    if (path.startsWith('/profile') || path.startsWith('/account')) return '/profile'
    if (path.startsWith('/login') || path.startsWith('/register')) return '/login'

    return null
  }, [router.pathname])

  const handleTabClick = useCallback((tab: TabItem) => {
    if (tab.requiresAuth && !authenticated) {
      openDialog(AuthDialog, { onClose: () => {} })

      return
    }

    if (tab.href === '/profile' && profile?.slug) {
      router.push(`/profile/${profile.slug}`)

      return
    }

    router.push(tab.href)
  }, [authenticated, openDialog, profile?.slug, router])

  return (
    <StyledBottomTabBar>
      {tabItems.map((tab) => {
        const isActive = activeTab === tab.href

        return (
          <StyledTab
            key={tab.href}
            onClick={() => handleTabClick(tab)}
            $active={isActive}
            role="tab"
            aria-selected={isActive}
            aria-label={tab.label}
          >
            {tab.customIcon
              ? tab.customIcon({ active: isActive })
              : (
                <StyledIcon
                  icon={isActive ? tab.activeIcon : tab.icon}
                  $active={isActive}
                />
              )}
            <StyledLabel $active={isActive}>{tab.label}</StyledLabel>
          </StyledTab>
        )
      })}
    </StyledBottomTabBar>
  )
}

const BOTTOM_TAB_HEIGHT = 56

export const bottomTabBarHeight = BOTTOM_TAB_HEIGHT

const StyledBottomTabBar = styled('nav')`
  display: flex;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${pxToRem(BOTTOM_TAB_HEIGHT)};
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background-color: ${props => props.theme.palette.primary.main};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  z-index: ${props => props.theme.zIndex.appBar};
  justify-content: space-around;
  align-items: center;

  ${props => props.theme.breakpoints.up('md')} {
    display: none;
  }
`

const StyledTab = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  border: none;
  background: none;
  cursor: pointer;
  padding: ${pxToRem(6)} 0;
  gap: ${pxToRem(2)};
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.7;
  }
`

const StyledIcon = styled(Icon, {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>`
  font-size: ${pxToRem(20)};
  color: ${props => props.$active ? '#F6a42A' : 'rgba(245, 242, 238, 0.6)'};
  transition: color 0.15s ease;
`

const QuillIconWrapper = styled('div')`
  height: ${pxToRem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
`

const StyledLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>`
  font-size: ${pxToRem(10)};
  font-weight: ${props => props.$active ? 600 : 400};
  color: ${props => props.$active ? '#F6a42A' : 'rgba(245, 242, 238, 0.6)'};
  transition: color 0.15s ease;
  line-height: 1;
`
